'use client'

import { useEffect, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useAuth } from '@/composable/useAuth'
import { isProtectedPath, isPublicPath, LOGIN_PATH } from '@/lib/auth-routes'

/**
 * Sustituye el middleware de Next en despliegues estáticos (`output: 'export'`, p. ej. Firebase Hosting):
 * misma matriz de rutas; redirección a login con `?from=` si no hay sesión Firebase.
 * La cookie `alumni_auth_session` sigue siendo un espejo en cliente (`useAuth`); el guard usa `user`.
 * Rutas públicas no bloquean; el login mantiene `redirectFromClaims` al tener sesión (sin duplicar aquí).
 */
export function AlumniSessionRouteGuard({ children }: Readonly<{ children: ReactNode }>) {
	const { pathname } = useLocation()
	const navigate = useNavigate()
	const { user, loading } = useAuth()

	useEffect(() => {
		if (loading) return

		if (!isProtectedPath(pathname)) return
		if (user) return
		const from = encodeURIComponent(pathname)
		navigate(`${LOGIN_PATH}?from=${from}`, { replace: true })
	}, [loading, user, pathname, navigate])

	if (isPublicPath(pathname)) {
		return <>{children}</>
	}

	if (isProtectedPath(pathname)) {
		if (loading) {
			return <div className="flex min-h-svh items-center justify-center p-8 text-zinc-400">Cargando sesión…</div>
		}
		if (!user) {
			return <div className="flex min-h-svh items-center justify-center p-8 text-zinc-400">Redirigiendo al inicio de sesión…</div>
		}
	}

	return <>{children}</>
}
